/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useState } from 'react';
import FormControl, { FormControlTypeMap } from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import debounce from 'lodash.debounce';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import CircularProgress from '../../_loading/CircularProgress';

export type IValue = {
  label: string;
  value: string;
  optionLabel?: string;
  data?: any;
  startComponent?: React.ReactNode;
};
export type IValues = IValue[];

const filter = createFilterOptions<IValue>();

export interface IProps<TValue extends IValue | IValues> {
  onChange: (values: TValue) => void;
  creatable?: boolean;
  async?: boolean;
  loadOptions?: (
    inputValue: string,
    callback?: (options: any) => void
  ) => Promise<any>;
  isMulti?: boolean;
  options?: {
    label: string;
    value?: any;
    options?: IValues;
  }[];
  label?: string;
  required?: boolean;
  margin?: FormControlTypeMap['props']['margin'];
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  loadingHeight?: number;
  isClearable?: boolean;
  value?: TValue | string | string[] | null;
  menuPosition?: string;
  placeholder?: string;
  menuPlacement?: string;
  menuPortalTarget?: any;

  [key: string]: any;
}

export default function Select<TValue extends IValue | IValues>(
  props: IProps<TValue>
) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [debounceKey, setDebounceKey] = useState(new Date().getTime());

  const {
    creatable,
    async,
    loadOptions,
    isMulti,
    options,
    onChange,
    label,
    required,
    margin,
    error,
    helperText,
    fullWidth,
    disabled,
    loading,
    loadingHeight,
    isClearable,
    value,
    menuPosition,
    placeholder,
    menuPlacement,
    menuPortalTarget,
  } = props;
  const [searchOptionResult, setSearchOptionResult] = useState<IValues>([]);

  const debounceSearchOptions = useCallback(
    debounce((input: string) => {
      if (!input) {
        return [];
      }

      if (loadOptions) {
        const promise = loadOptions(input);
        if (promise) {
          setDebounceKey(new Date().getTime());
          return promise.then((res: any) => setSearchOptionResult(res));
        }
      }
      return [];
    }, 800),
    [debounceKey]
  );

  useEffect(() => {
    if (inputValue) {
      debounceSearchOptions(inputValue);
    }
  }, [inputValue]);

  function getSelectedOption() {
    if (value === undefined) {
      if (isMulti) return [];
      return null;
    }

    if (value === '') {
      return null;
    }

    if (options) {
      if (
        typeof value === 'string' ||
        typeof value === 'boolean' ||
        (value && value.constructor !== Array && typeof value !== 'object')
      ) {
        const tempOptions = (options as any)
          .map((option: any) => (option.options ? option.options : option))
          .flat();
        // @ts-ignore
        return tempOptions.find((option) => option.value === value);
      }

      if (value && value.constructor === Array) {
        if (value.length === 0) return [];

        const tempOptions = (options as any)
          .map((option: any) => (option.options ? option.options : option))
          .flat();

        const values =
          typeof value[0] === 'string' ? value : value.map((i: any) => i.value);

        // @ts-ignore
        return tempOptions.filter((option) => values.includes(option.value));
      }
    }

    return value;
  }

  let isGroupBy = false;
  const groupOptions: any = [];
  if (options && options.length) {
    // @ts-ignore
    isGroupBy = Boolean(options.find((option: any) => option.options));

    if (isGroupBy) {
      options.forEach((option: any) => {
        if (option.options) {
          option.options.forEach((option2: any) => {
            groupOptions.push({
              ...{
                groupLabel: option.label,
              },
              ...option2,
            });
          });
        }
      });
    }
  }

  const valueProp = getSelectedOption() || null;

  const autoCompleteProps: any = {
    inputValue,
    ChipProps: {
      color: 'primary',
    },
    onInputChange: (event: any, newValue: string | null, reason: string) => {
      setInputValue(newValue || '');
    },
    options: isGroupBy ? groupOptions : options || [],
    placeholder,
    value: valueProp,
    clearOnBlur: true,
    onChange: (event: any, newValue: any) => {
      if (onChange) {
        if (typeof newValue === 'string') {
          const tempValue = valueProp;

          if (tempValue && tempValue.constructor === Array) {
            tempValue.push({
              label: newValue,
              value: newValue,
            });
            onChange(tempValue as any);
          } else {
            onChange({
              label: newValue,
              value: newValue,
            } as any);
          }
        } else {
          onChange(newValue);
        }
      }
    },
    isOptionEqualToValue: (option: any, v: any) => option.value === v,
    multiple: isMulti,
    disabled,
    renderOption: (tempProps: any, data: any) => (
      <MenuItem {...tempProps}>
        {data && data.startComponent && <>{data.startComponent}&nbsp;&nbsp;</>}
        <span>{data.optionLabel ? data.optionLabel : data.label}</span>
        {data && data.endComponent && <>&nbsp;&nbsp;{data.endComponent}</>}
      </MenuItem>
    ),
    getOptionLabel: (option: IValue) => option.label,
    groupBy: isGroupBy ? (option: any) => option.groupLabel : undefined,
    getOptionDisabled: (option: IValue) => {
      const tempValue = valueProp;

      if (tempValue) {
        if (tempValue.constructor === Array) {
          return Boolean(tempValue.find((o) => o.value === option.value));
        }
        if (typeof tempValue === 'object') {
          return option.value === tempValue.value;
        }
        return option.value === tempValue;
      }
      return false;
    },
  };
  const autoCompleteCreatableProps: any = {
    filterOptions: (tempOptions: any[], params: any) => {
      let filtered = filter(tempOptions, params);
      let values: IValue[] = [];

      const tempValue = valueProp;

      if (tempValue) {
        if (tempValue.constructor === Array) {
          values = tempValue;
        } else if (typeof tempValue === 'object') {
          values = [tempValue];
        } else {
          values = [
            {
              label: tempValue,
              value: tempValue,
            },
          ];
        }
      }

      if (
        !filtered.find(
          (o) =>
            o.value === params.inputValue ||
            o.label.toLowerCase() === params.inputValue.toLowerCase()
        ) &&
        !values.find(
          (o) =>
            o.value === params.inputValue ||
            o.label.toLowerCase() === params.inputValue.toLowerCase()
        ) &&
        params.inputValue !== ''
      ) {
        filtered = [
          ...[
            {
              optionLabel: `${t('add')} "${params.inputValue}"`,
              value: params.inputValue,
              label: params.inputValue,
            },
          ],
          ...filtered,
        ];
      }

      return filtered;
    },
  };

  const autoCompleteTextFieldProps = {
    label,
    error: Boolean(helperText),
    helperText,
    placeholder,
  };

  let SelectComponent: any = null;

  if (async && loadOptions && creatable) {
    // SelectComponent = (
    //     // @ts-ignore
    //     <AsyncCreatableSelect
    //         loadOptions={debounceLoadOptions}
    //         cacheOptions
    //         {...selectProps}
    //     />
    // );
    SelectComponent = (
      <Autocomplete
        {...autoCompleteProps}
        {...autoCompleteCreatableProps}
        options={searchOptionResult}
        renderInput={(params) => (
          <TextField
            {...params}
            {...autoCompleteTextFieldProps}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  } else if (async && loadOptions) {
    // SelectComponent = (
    //     // @ts-ignore
    //     <Async loadOptions={debounceLoadOptions} {...selectProps} cacheOptions/>
    // );
    SelectComponent = (
      <Autocomplete
        {...autoCompleteProps}
        filterOptions={(x) => x}
        options={searchOptionResult}
        renderInput={(params) => (
          <TextField
            {...params}
            {...autoCompleteTextFieldProps}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  } else if (creatable) {
    // @ts-ignore
    // SelectComponent = <Creatable {...selectProps} />;
    SelectComponent = (
      <Autocomplete
        {...autoCompleteProps}
        {...autoCompleteCreatableProps}
        renderInput={(params) => (
          <TextField {...params} {...autoCompleteTextFieldProps} />
        )}
      />
    );
  } else {
    // @ts-ignore
    // SelectComponent = <ReactSelect {...selectProps} />;

    SelectComponent = (
      <Autocomplete
        {...autoCompleteProps}
        renderInput={(params) => (
          <TextField {...params} {...autoCompleteTextFieldProps} />
        )}
      />
    );
  }

  return (
    <FormControl fullWidth margin={margin}>
      {SelectComponent}
    </FormControl>
  );
}
